/**
 * useTheme Hook
 *
 * React hook to access theme colors and schemes throughout the application
 * Listens to theme changes and re-renders components when theme changes
 * Usage: const { colors, colorSchemes } = useTheme();
 */

import { useState, useEffect } from 'react';

// Theme Definitions
const THEME_DEFINITIONS = {
  blue: {
    primary: {
      dark: '#2c3e50',
      main: '#486BAD',
      light: '#5B8FC7',
      lighter: '#7BA3D9',
    },
  },
  magenta: {
    primary: {
      dark: '#9D1F3A',
      main: '#D84565',
      light: '#E5729B',
      lighter: '#F0A3BF',
    },
  },
  indigo: {
    primary: {
      dark: '#1A1F2E',
      main: '#2D3047',
      light: '#434960',
      lighter: '#6A7590',
    },
  },
};

function getThemeColors() {
  let activeTheme = 'blue';

  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('appTheme');
    if (saved && THEME_DEFINITIONS[saved as keyof typeof THEME_DEFINITIONS]) {
      activeTheme = saved;
    }
  }

  const themeColor = THEME_DEFINITIONS[activeTheme as keyof typeof THEME_DEFINITIONS];

  return {
    primary: themeColor.primary,
    background: {
      primary: '#ffffff',
      secondary: '#f8f9fc',
      tertiary: '#eff6ff',
    },
    border: {
      primary: themeColor.primary.main,
      secondary: '#d1d5db',
      light: '#e5e7eb',
    },
    text: {
      primary: themeColor.primary.dark,
      secondary: '#6b7280',
      muted: '#777879ff',
      light: '#9ca3af',
    },
    status: {
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
    },
    shadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    borderRadius: '12px',
    borderRadiusSm: '0.5rem',
    borderRadiusMd: '0.75rem',
  };
}

function getColorSchemes(colors: ReturnType<typeof getThemeColors>) {
  return {
    card: {
      backgroundColor: colors.background.primary,
      borderColor: colors.border.secondary,
      border: `1px solid ${colors.border.secondary}`,
      boxShadow: colors.shadow,
    },

    cardDark: {
      backgroundColor: colors.background.secondary,
      borderColor: colors.border.primary,
      border: `1px solid ${colors.border.primary}`,
      boxShadow: colors.shadow,
    },

    selectControl: {
      borderColorFocused: colors.primary.main,
      borderColorUnfocused: colors.border.secondary,
      backgroundColor: colors.background.primary,
      shadowFocused: `0 0 0 3px rgba(${hexToRgb(colors.primary.main)}, 0.1)`,
    },

    selectOption: {
      backgroundSelected: colors.primary.main,
      backgroundFocused: colors.background.secondary,
      backgroundDefault: colors.background.primary,
      colorSelected: colors.background.primary,
      colorDefault: colors.text.primary,
    },

    selectMenu: {
      backgroundColor: colors.background.primary,
      borderColor: colors.border.secondary,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },

    buttonPrimary: {
      backgroundColor: colors.primary.main,
      color: colors.background.primary,
      borderColor: colors.primary.main,
    },

    buttonSecondary: {
      backgroundColor: colors.background.secondary,
      color: colors.primary.dark,
      borderColor: colors.border.primary,
    },

    input: {
      backgroundColor: colors.background.primary,
      borderColor: colors.border.secondary,
      borderColorFocused: colors.primary.main,
      textColor: colors.text.primary,
      placeholderColor: colors.text.muted,
    },

    navbar: {
      backgroundColor: colors.background.primary,
      textColor: colors.text.primary,
      borderColor: colors.border.secondary,
      activeBackground: colors.background.secondary,
      activeTextColor: colors.primary.main,
    },

    table: {
      headerBackground: colors.background.secondary,
      headerTextColor: colors.text.primary,
      rowBorder: colors.border.secondary,
      hoverRowBackground: colors.background.secondary,
    },
  };
}

// Helper function to convert hex to rgb
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  }
  return '72, 107, 173'; // Default blue
}

export const useTheme = () => {
  const [colors, setColors] = useState(getThemeColors());
  const [colorSchemes, setColorSchemes] = useState(getColorSchemes(colors));

  useEffect(() => {
    // Update colors when component mounts
    const updatedColors = getThemeColors();
    setColors(updatedColors);
    setColorSchemes(getColorSchemes(updatedColors));

    // Listen for storage changes (theme switcher changes)
    const handleStorageChange = () => {
      const updatedColors = getThemeColors();
      setColors(updatedColors);
      setColorSchemes(getColorSchemes(updatedColors));
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom theme change events
    const handleThemeChange = () => {
      const updatedColors = getThemeColors();
      setColors(updatedColors);
      setColorSchemes(getColorSchemes(updatedColors));
    };

    window.addEventListener('themeChanged', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  return {
    colors,
    colorSchemes,
  };
};

export default useTheme;
